import { User } from 'library/types';
import { renderFormattedDate } from 'utils';
import { FormattedMessage } from 'react-intl';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ProjectStepCommentItem } from 'pages/project-step-managing/types';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';

type CommentsCellProps<RowType extends object = object> = {
  row: RowType;
  comments?: ProjectStepCommentItem[];
  currentUserId: number | string;
  onAddComment: (row: RowType, anchorEl: HTMLElement) => void;
  onDeleteComment: (comment: ProjectStepCommentItem, row: RowType) => void;
  users: User[];
};

export const CommentsCell = <RowType extends object = object>({
  row,
  comments = [],
  currentUserId,
  onAddComment,
  onDeleteComment,
  users,
}: CommentsCellProps<RowType>) => {
  return (
    <Box
      sx={{
        width: '100%',
        py: 1,
        pr: 5,
        position: 'relative',
        overflow: 'hidden',
        '&:hover .comments-add-action, &:focus-within .comments-add-action': {
          opacity: 1,
          pointerEvents: 'auto',
        },
      }}>
      <Box
        className="comments-add-action"
        sx={{
          position: 'absolute',
          top: 0,
          right: 6,
          opacity: 1,
          pointerEvents: 'auto',
          transition: 'opacity 0.2s ease',
          zIndex: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          '@media (hover: hover) and (pointer: fine)': {
            opacity: 0,
            pointerEvents: 'none',
          },
        }}>
        <Tooltip title={<FormattedMessage id="projectStepManaging.addComment" defaultMessage="Yorum Ekle" />}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onAddComment(row, e.currentTarget);
            }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {comments.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          <FormattedMessage id="projectStepManaging.noCommentYet" defaultMessage="Henüz yorum eklenmemiş" />
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {comments.map((item) => {
            const isOwner = String(item.userId) === String(currentUserId);

            return (
              <Box
                key={item.id}
                sx={{
                  position: 'relative',
                  pr: isOwner ? 4 : 0,
                  borderRadius: 1,
                  '&:hover .comment-delete-action, &:focus-within .comment-delete-action': {
                    opacity: isOwner ? 1 : 0,
                    pointerEvents: isOwner ? 'auto' : 'none',
                  },
                }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    pr: isOwner ? 1 : 0,
                  }}>
                  {renderFormattedDate(item.createdAt, true)} •{' '}
                  {users.find((user) => user.id === item.userId)?.nameSurname}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.35,
                  }}>
                  {item.commentText}
                </Typography>

                {isOwner && (
                  <Box
                    className="comment-delete-action"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 10,
                      opacity: 1,
                      pointerEvents: 'auto',
                      transition: 'opacity 0.2s ease',
                      '@media (hover: hover) and (pointer: fine)': {
                        opacity: 0,
                        pointerEvents: 'none',
                      },
                    }}>
                    <Tooltip
                      title={<FormattedMessage id="projectStepManaging.deleteComment" defaultMessage="Yorum Sil" />}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteComment(item, row);
                        }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default CommentsCell;
